import { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight } from "lucide-react";
import PageRoutes from "@/utils/constants";
import { BackButton } from "@/v2/components/BackButton";
import { useChannel, useUpdateChannelPreferences } from "./api";
import { SeasonColumn } from "./components/SeasonColumn";
import { TeamColumn } from "./components/TeamColumn";
import { PlayersColumn } from "./components/PlayersColumn";
import { ActionsColumn } from "./components/ActionsColumn";
import { SummaryFooter } from "./components/SummaryFooter";
import type { ChannelConfiguration, Season } from "./types";

type SelectPreferencesStepProps = {
  onSubmitSuccess?: () => void;
};

export function SelectPreferencesStep({
  onSubmitSuccess,
}: SelectPreferencesStepProps) {
  const navigate = useNavigate();
  const { channel_id }: any = useParams();
  const { data: channel } = useChannel(channel_id);
  const updatePreferences = useUpdateChannelPreferences(channel_id);

  const [channelName, setChannelName] = useState("");
  const [selectedSeason, setSelectedSeason] = useState<Season[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<(string | number)[]>([]);
  const [selectAllTeamPlayers, setSelectAllTeamPlayers] = useState<
    (string | number)[]
  >([]);
  const [selectedPlayer, setSelectedPlayer] = useState<(string | number)[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string[]>([]);
  const [isSelectAllEvent, setIsSelectAllEvent] = useState(false);

  useEffect(() => {
    const config = channel?.Configuration;
    if (!config) return;
    setChannelName(config.Name ?? "");
    setSelectedTeam(config.Teams ?? []);
    setSelectedSeason(config.Seasons ?? []);
    setSelectedEvent(config.Events ?? []);
    setSelectedPlayer(config.Players ?? []);
    setSelectAllTeamPlayers(config.All ?? []);
    if ((config.Events ?? []).length === 0) {
      setIsSelectAllEvent(true);
    }
  }, [channel]);

  function currentConfig(
    overrides: Partial<ChannelConfiguration> = {},
  ): ChannelConfiguration {
    return {
      Name: channelName,
      Teams: selectedTeam,
      Players: selectedPlayer,
      Events: selectedEvent,
      All: selectAllTeamPlayers,
      Seasons: selectedSeason,
      ...overrides,
    };
  }

  function autosave(overrides: Partial<ChannelConfiguration>) {
    updatePreferences
      .mutateAsync(currentConfig(overrides))
      .catch((err: any) => {
        toast.error(
          err?.response?.data?.message ??
            "Couldn't save your change. Please try again.",
        );
      });
  }

  function handleSeasonChange(next: Season[]) {
    setSelectedSeason(next);
    autosave({ Seasons: next });
  }

  function handleTeamChange(next: {
    teams: (string | number)[];
    all: (string | number)[];
  }) {
    setSelectedTeam(next.teams);
    setSelectAllTeamPlayers(next.all);
    autosave({ Teams: next.teams, All: next.all });
  }

  function handleBack() {
    navigate(
      PageRoutes.MyNAHLTV.replace(":id", "1").replace(
        ":channel_id",
        channel_id,
      ),
    );
  }

  async function handleSubmit() {
    try {
      await updatePreferences.mutateAsync(currentConfig());
      onSubmitSuccess?.();
      navigate(PageRoutes.PersonalChannel.replace(":id", channel_id));
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ??
          "Couldn't build your Spotlight. Please try again.",
      );
    }
  }

  const seasonDone = selectedSeason.length > 0;
  const teamDone = selectedTeam.length > 0;
  const playersDone =
    selectedPlayer.length > 0 || selectAllTeamPlayers.length > 0;
  const actionsDone = selectedEvent.length > 0 || isSelectAllEvent;

  const actionsCount =
    isSelectAllEvent && selectedEvent.length === 0
      ? "All"
      : `${selectedEvent.length} selected`;

  const steps = [
    {
      key: "season",
      label: "Season",
      done: seasonDone,
      subtext: seasonDone
        ? `${selectedSeason.length} selected`
        : "Not selected",
    },
    {
      key: "teams",
      label: "Teams",
      done: teamDone,
      subtext: teamDone ? `${selectedTeam.length} selected` : "Not selected",
    },
    {
      key: "players",
      label: "Players",
      done: playersDone,
      subtext: playersDone ? "Selected" : "Optional",
    },
    {
      key: "actions",
      label: "Actions",
      done: actionsDone,
      subtext: actionsDone ? actionsCount : "Pick events",
    },
  ];
  // Purely a visual "you are here" cue for the strip below — the first not-yet-done
  // step is highlighted the way the reference design marks a current step. This does
  // not gate anything: every column stays visible and editable regardless of state.
  const currentIndex = steps.findIndex((step) => !step.done);

  return (
    // text-left overrides the v1 wizard shell's `<section className="text-center">`
    // ancestor: text-align is inherited, so a short heading in a flex-col box that
    // stretches to a longer sibling's width (e.g. this title next to the
    // description) would otherwise center within that stretched box instead of
    // sitting flush left.
    <div className="flex flex-col gap-2 text-left">
      <div className="flex flex-col">
        <BackButton
          onClick={() => navigate(PageRoutes.LensStudio)}
          label="Back to Studio"
          className="text-action hover:text-action text-sm font-bold self-start justify-start"
        />
        <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
          Step 2 of 2
        </div>
        <h1
          className="font-extrabold text-slate-900"
          style={{ fontSize: "24px", lineHeight: 1.2 }}
        >
          Build a Spotlight
        </h1>
        <p className="text-sm text-slate-500" style={{ marginTop: "2px" }}>
          Pick what you want to follow. We'll gather every matching clip and
          keep it updated automatically as new games are played.
        </p>
      </div>

      {/* Each step is an equal-width cell (flex-1) rather than a
          content-width pill, so the current step's fill starts at its cell
          edge and covers the whole cell instead of floating inside it. The
          chevrons are separators between cells, so they sit outside them. */}
      <div
        className="rounded-xl border border-slate-200 bg-card flex flex-wrap items-stretch"
        style={{ padding: "6px", gap: "4px" }}
      >
        {steps.map((step, i) => {
          const isCurrent = i === currentIndex;
          return (
            <Fragment key={step.key}>
              <div
                data-testid={`progress-${step.key}`}
                data-done={step.done}
                className={`flex flex-1 min-w-[150px] items-center gap-2 rounded-lg ${isCurrent ? "bg-action-light" : ""}`}
                style={{ padding: "6px 10px" }}
              >
                <span
                  className={`flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold shrink-0 ${
                    step.done
                      ? "bg-success text-white"
                      : isCurrent
                        ? "bg-action text-white"
                        : "bg-muted text-slate-500"
                  }`}
                >
                  {step.done ? "✓" : i + 1}
                </span>
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-bold text-slate-900 truncate">
                    {step.label}
                  </span>
                  <span className="text-xs text-slate-500 truncate">
                    {step.subtext}
                  </span>
                </div>
              </div>
              {i < steps.length - 1 && (
                <ChevronRight
                  className="w-5 h-5 text-slate-300 shrink-0 self-center"
                  aria-hidden="true"
                />
              )}
            </Fragment>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        <SeasonColumn
          selectedSeason={selectedSeason}
          onSelectionChange={handleSeasonChange}
        />
        <TeamColumn
          selectedTeam={selectedTeam}
          selectAllTeamPlayers={selectAllTeamPlayers}
          onSelectionChange={handleTeamChange}
        />
        <PlayersColumn
          selectedTeam={selectedTeam}
          selectedPlayer={selectedPlayer}
          setSelectedPlayer={setSelectedPlayer}
          selectAllTeamPlayers={selectAllTeamPlayers}
          setSelectAllTeamPlayers={setSelectAllTeamPlayers}
        />
        <ActionsColumn
          selectedEvent={selectedEvent}
          setSelectedEvent={setSelectedEvent}
          isSelectAllEvent={isSelectAllEvent}
          setIsSelectAllEvent={setIsSelectAllEvent}
        />
      </div>

      <SummaryFooter
        selectedSeason={selectedSeason}
        selectedTeam={selectedTeam}
        selectedPlayer={selectedPlayer}
        selectAllTeamPlayers={selectAllTeamPlayers}
        selectedEvent={selectedEvent}
        isSelectAllEvent={isSelectAllEvent}
        isSubmitting={updatePreferences.isPending}
        onBack={handleBack}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
